import {useEffect, useState} from 'react';

import useParseURL from 'hooks/useParseURL';

import {inputMenuActionType, SWRKeyEnum} from 'interfaces';
import useIsMounted from 'hooks/useIsMounted';
import {useGetCompanies, useGetPlanFishTypes, useGetSmoltSupply} from 'hooks/swr';
import {HarvestDemandPutSpeciesEnum, type SmoltSupplyPost, type SmoltSupplyPut} from 'client';

import useGetPlanTreeNavigation from 'hooks/plan/useGetPlanTreeNavigation';

import {useInputMenuContext} from 'context/InputMenu';
import backendCommunicationInstance from 'services/backendCommunicator';
import {dateReader, kilosToGrams, gramsToKilos, localDateSender} from 'helpers/UnitsFormatHandler';
import Alert from 'helpers/Alert';
import {mutate} from 'swr';
import {idNamesToIds} from 'utils/idNamesToIds';

export const useAddEditSmoltSupply = () => {
  const {planId} = useParseURL();
  const isMounted = useIsMounted();

  const [createUpdateSmoltAvailability, setCreateUpdateSmoltAvailability] = useState(false);
  const [smoltSupply, setSmoltSupply] = useState<SmoltSupplyForm>({
    startDate: new Date(),
    endDate: new Date(),
    species: HarvestDemandPutSpeciesEnum.Salmon,
    certificates: [],
    fishGroupPropertyIds: [],
    siteIds: [],
  });
  const {data: companies} = useGetCompanies({resourceId: planId});
  const {planTreeNavigation} = useGetPlanTreeNavigation({planId, isMounted});
  const {data: fishTypes} = useGetPlanFishTypes({resourceId: planId});
  const [isLoading, setIsLoading] = useState(false);
  const inputMenuContext = useInputMenuContext();
  const inputMenuDispatch = inputMenuContext?.dispatch;
  const smoltId = inputMenuContext?.state.formData?.id;

  const {data} = useGetSmoltSupply({resourceId: smoltId ? [planId, smoltId] : undefined});

  useEffect(() => {
    if (companies) {
      const defaultCompany = companies.find((company) => company.isDefault);
      if (defaultCompany && !smoltSupply.companyId) {
        setSmoltSupply({
          ...smoltSupply,
          companyId: defaultCompany?.id,
        });
      }
    }
  }, [companies, smoltSupply]);

  useEffect(() => {
    if (data) {
      const {weight, startDate, endDate, fishGroupProperties, sites, company, fishType, ...restData} = data;
      setSmoltSupply({
        ...restData,
        weight: kilosToGrams(weight),
        startDate: dateReader(startDate),
        endDate: dateReader(endDate),
        fishGroupPropertyIds: idNamesToIds(fishGroupProperties),
        siteIds: idNamesToIds(sites),
        companyId: company.id,
        fishTypeId: fishType.id,
      });
    }
  }, [data]);

  useEffect(() => {
    const writeSmoltSupply = !smoltId && smoltForm2Write(smoltSupply, planId);
    if (createUpdateSmoltAvailability && writeSmoltSupply) {
      setIsLoading(true);
      backendCommunicationInstance
        .addSmoltSupply(writeSmoltSupply)
        .then(() => {
          if (isMounted.current) {
            void mutate([planId, SWRKeyEnum.GET_PLAN_SMOLT_SUPPLY]);
            inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
          }
          setIsLoading(false);
        })
        .catch((error) => {
          if (isMounted.current) {
            Alert.error(error);
          }
          setIsLoading(false);
        });
    }
    const updateSmoltSupply = smoltForm2Update(smoltSupply, planId);
    if (smoltId && createUpdateSmoltAvailability && updateSmoltSupply) {
      setIsLoading(true);
      backendCommunicationInstance
        .updateSmoltSupply(smoltId, updateSmoltSupply)
        .then(() => {
          void mutate([planId, SWRKeyEnum.GET_PLAN_SMOLT_SUPPLY]);
          inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
          setIsLoading(false);
        })
        .catch((error) => {
          if (isMounted.current) {
            Alert.error(error);
          }
          setIsLoading(false);
        });
    }
    if (createUpdateSmoltAvailability) {
      setCreateUpdateSmoltAvailability(false);
    }
  }, [createUpdateSmoltAvailability]);

  return {
    smoltSupply,
    setSmoltSupply,
    planId,
    fishTypes,
    planTreeNavigation,
    smoltForm2Write,
    setCreateUpdateSmoltAvailability,
    isLoading,
  };
};

declare type SmoltSupplyForm = Partial<
  Omit<SmoltSupplyPost, 'startDate' | 'endDate' | 'species' | 'certificates' | 'fishGroupPropertyIds'> & {
    startDate: Date | null;
    endDate: Date | null;
  }
> & {
  species: SmoltSupplyPost['species'];
  certificates: SmoltSupplyPost['certificates'];
  fishGroupPropertyIds: SmoltSupplyPost['fishGroupPropertyIds'];
};

const smoltForm2Write = (smoltSupplyForm: SmoltSupplyForm, planId: string): SmoltSupplyPost | undefined => {
  const {count, weight, fishTypeId, siteIds, startDate, endDate, assignedToAll, companyId, name, newLinkedInputs} =
    smoltSupplyForm;

  if (
    fishTypeId === undefined ||
    count === undefined ||
    weight === undefined ||
    !startDate ||
    !endDate ||
    !companyId ||
    !name ||
    ((!siteIds || siteIds.length === 0) && !assignedToAll)
  ) {
    return;
  }

  try {
    const smoltSupplyWrite: SmoltSupplyPost = {
      ...smoltSupplyForm,
      name,
      siteIds: siteIds ?? [],
      fishTypeId,
      count,
      weight: gramsToKilos(weight),
      startDate: localDateSender(startDate),
      endDate: localDateSender(endDate),
      planId,
      assignedToAll: !!assignedToAll,
      companyId,
      newLinkedInputs: newLinkedInputs ?? [],
    };
    return smoltSupplyWrite;
  } catch (error) {}
};

const smoltForm2Update = (smoltSupplyForm: SmoltSupplyForm, planId: string): SmoltSupplyPut | undefined => {
  const {count, weight, fishTypeId, siteIds, startDate, endDate, assignedToAll, companyId, species, name} =
    smoltSupplyForm;

  if (
    fishTypeId === undefined ||
    count === undefined ||
    weight === undefined ||
    !startDate ||
    !endDate ||
    !companyId ||
    !species ||
    !name ||
    ((!siteIds || siteIds.length === 0) && !assignedToAll)
  ) {
    return;
  }

  try {
    const smoltSupplyWrite: SmoltSupplyPut = {
      ...smoltSupplyForm,
      name,
      planId,
      companyId,
      siteIds: siteIds ?? [],
      fishTypeId,
      count,
      assignedToAll: !!assignedToAll,
      species,
      certificates: smoltSupplyForm.certificates ?? [],
      weight: gramsToKilos(weight),
      startDate: localDateSender(startDate),
      endDate: localDateSender(endDate),
    };
    return smoltSupplyWrite;
  } catch (error) {}
};
